import {
  CreateDataBlockTypeDto,
  DataBlockTypeListDto,
  DataBlockVariant,
  QueryFactory,
} from '@lmt/shared-types';
import { AutoSizedTableWrapper } from '@lmt/unicorn-shared/app/components/common/AutoSizedTableWrapper';
import { useModal } from '@lmt/unicorn-shared/app/components/common/ui-kit/modal/useModal';
import { parseError } from '@lmt/unicorn-shared/app/helpers/error-handling';
import { mapExcelRowToDataGrid } from '@lmt/unicorn-shared/app/helpers/excel-to-grid-helper';
import { convertToErrorString } from '@lmt/unicorn-shared/app/helpers/form/useErrorHandler';
import {
  NS_DataBlockFields,
  useScopedTranslation,
} from '@lmt/unicorn-shared/app/helpers/localization/useScopedTranslation';
import { nameofFactory } from '@lmt/unicorn-shared/app/helpers/nameof';
import { useEventCallback } from '@mui/material';
import React, { FC, useCallback, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  DataGrid,
  DXG,
  EditingStartEvent,
  InitNewRowEvent,
  RowInsertingEvent,
  RowRemovingEvent,
  RowUpdatingEvent,
  ToolbarPreparingEvent,
  useDataGrid,
} from '../../common/devexpress/Grid/dx-grid';
import {
  allowOnlySingleNewRow,
  useCachedDataSourceWhenEditing,
  useParallelSaving,
} from '../../common/devexpress/Grid/grid-helper';
import { useClaims } from '../../features/auth/operations';
import { createId } from '../../helpers/create-id';
import { useDataGridExportHandler } from '../../helpers/devextreme-helper';
import { readFileAsExcel } from '../../helpers/file-helper';
import { Loading } from '../common/Loading';
import {
  dataBlockVariants,
  getDataBlockTypeNameForLocalization,
} from '../measurements/common';
import styles from './DataBlockTypes.module.scss';
import { useDeviceModels } from '../../features/mappings/operations';

type Row = {
  row: RowData;
};
type RowData = {
  data: DataBlockTypeListDto;
  key: number;
  removed: boolean;
  label?: string;
  isNewRow: boolean;
};

function isDeleteVisible(row: Row) {
  const data = row.row.data;

  return !data.isUsed && !data.isDefault && !row.row.removed;
}

function isActivateVisible(row: Row) {
  const data = row.row.data;

  return data.isDeactivated;
}

function isDeactivateVisible(row: Row) {
  const data = row.row.data;

  return !data.isDeactivated;
}

function isRenameVisible(row: Row) {
  const data = row.row.data;

  return !data.isDefault && !row.row.isNewRow;
}

const nameof = nameofFactory<DataBlockTypeListDto>();

export const DataBlockTypes: FC = React.memo(function DataBlockTypes() {
  const i18n = useTranslation();
  const i18nDataBlockTypes = useScopedTranslation(
    'data_block_types',
    NS_DataBlockFields,
  );
  const claims = useClaims();
  const deviceModels = useDeviceModels();

  useDataGrid();
  const dataGridRef = useRef<DataGrid<DataBlockTypeListDto, number>>(null);
  const dataBlockTypesQuery =
    QueryFactory.DataBlockTypeQuery.useGetDataBlockTypesForEditingQuery();

  const parallelSaving = useParallelSaving(dataGridRef);
  const onRowCreate = useEventCallback(
    (row: RowInsertingEvent<DataBlockTypeListDto>) => {
      async function onRowCreateAsync(data: DataBlockTypeListDto) {
        const dto: CreateDataBlockTypeDto = {
          value: data.value,
          variant: data.variant,
          device: data.device,
          label: data.label,
        };
        const finishLoading = parallelSaving.startLoading();
        try {
          await QueryFactory.DataBlockTypeClient.create(dto);
        } catch (error) {
          await modals.showError({ text: parseError(error) });
          return true;
        } finally {
          finishLoading();
        }
        await refetchQuery();
        return false;
      }

      row.cancel = onRowCreateAsync(row.data);
    },
  );

  const onRowUpdate = useEventCallback(
    async (row: RowUpdatingEvent<DataBlockTypeListDto>) => {
      async function onUpdateAsync(
        data: RowUpdatingEvent<DataBlockTypeListDto>,
      ) {
        const finishLoading = parallelSaving.startLoading();
        try {
          if (data.newData.label !== undefined) {
            await QueryFactory.DataBlockTypeClient.patch(data.oldData.id, {
              label: data.newData.label.trim(),
            });
          }
        } catch (error) {
          await modals.showError({ text: parseError(error) });
          return true;
        } finally {
          finishLoading();
        }
        return false;
      }

      row.cancel = onUpdateAsync(row);
    },
  );

  const onRowDelete = useEventCallback(
    (row: RowRemovingEvent<DataBlockTypeListDto>) => {
      async function onDeleteAsync(
        row: RowRemovingEvent<DataBlockTypeListDto>,
      ) {
        const finishLoading = parallelSaving.startLoading();
        try {
          const id = row.key;
          await QueryFactory.DataBlockTypeClient.remove(id);
        } catch (error) {
          await modals.showError({ text: parseError(error) });
          return true;
        } finally {
          finishLoading();
        }
        await refetchQuery();
        return false;
      }

      row.cancel = onDeleteAsync(row);
    },
  );

  const refetchQuery = async () => {
    await dataBlockTypesQuery.refetch();
  };

  const onActivate = useCallback(async (row: Row) => {
    const finishLoading = parallelSaving.startLoading();
    try {
      const data = row.row.data;

      await QueryFactory.DataBlockTypeClient.activate(data.id);
    } catch (error) {
      await modals.showError({ text: parseError(error) });
    } finally {
      finishLoading();
    }
    await refetchQuery();
  }, []);

  const onDeactivate = useCallback(async (row: Row) => {
    const finishLoading = parallelSaving.startLoading();
    try {
      const data = row.row.data;

      await QueryFactory.DataBlockTypeClient.deactivate(data.id);
    } catch (error) {
      await modals.showError({ text: parseError(error) });
    } finally {
      finishLoading();
    }
    await refetchQuery();
  }, []);

  const onRowActivate = useCallback((row: Row) => {
    onActivate(row).catch((e) => console.error('onRowActivate', e));
  }, []);

  const onRowDeactivate = useCallback((row: Row) => {
    onDeactivate(row).catch((e) => console.error('onRowDeactivate', e));
  }, []);

  const inputFileRef = useRef<HTMLInputElement>(null);
  const [isExcelLoading, setIsExcelLoading] = useState(false);

  async function onImportExcelFile(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target!.files![0];
    if (!file) {
      return;
    }
    setIsExcelLoading(true);

    try {
      const values = await readFileAsExcel(file);

      const dataGrid = dataGridRef.current!.instance();
      for (let i = 2; i < values.length; i++) {
        const row = values[i];
        try {
          const newRow = await mapExcelRowToDataGrid<DataBlockTypeListDto>(
            dataGrid,
            row,
          );
          if (
            (dataBlockTypesQuery.data ?? []).find(
              (x) => x.value === newRow.value,
            )
          ) {
            // do not insert duplicate data block types
            continue;
          }
          const initNewRow = (e: any) => {
            Object.assign(e.data, newRow);
          };
          dataGrid.on('initNewRow', initNewRow);
          await dataGrid.addRow();
          dataGrid.off('initNewRow', initNewRow);
        } catch (e) {
          await modals.showError({
            text: `Error parsing excel row ${i}: ${convertToErrorString(e)}`,
          });
          return;
        }
      }
    } finally {
      setIsExcelLoading(false);
      // to allow choosing the same file again
      inputFileRef.current!.value = '';
    }
  }

  const dataBlockTypes = useMemo(
    () =>
      dataBlockVariants.map((variant) => {
        return {
          value: variant,
          displayValue: i18n.t(`data_block_type.${variant}`),
        };
      }),
    [dataBlockVariants],
  );

  const onToolbarPrerendering = useEventCallback(
    (e: ToolbarPreparingEvent<DataBlockTypeListDto>) => {
      if (claims.permissions.dataBlockTypeManagement) {
        e.toolbarOptions!.items!.push({
          location: 'after',
          widget: 'dxButton',
          options: {
            width: 136,
            text: i18n.t('table.import_button'),
            onClick: () => inputFileRef.current!.click(),
          },
        });
      }
      allowOnlySingleNewRow(e);
    },
  );
  const onInitNewRow = useEventCallback(
    (e: InitNewRowEvent<DataBlockTypeListDto>) => {
      e.data.id = createId() as any;
      e.data.value = e.data.value ?? '';
      e.data.label = e.data.label ?? '';
    },
  );
  const onEditorPreparing = useCallback((e: any) => {
    if (
      e.dataField === 'value' ||
      e.dataField === 'variant' ||
      e.dataField === 'device'
    ) {
      // we don't allow editing 'value', 'variant' and 'deviceModel' for existing data block types
      e.editorOptions.readOnly = Number.isInteger(e.row?.data.id);
    }
  }, []);
  const onOptionsChange = useCallback((props: any) => {
    if (
      props.name === 'columns' &&
      props.fullName?.includes('sortOrder') &&
      !props.value
    ) {
      props.component?.columnOption('id', 'sortOrder', 'desc');
    }
  }, []);
  const modals = useModal();
  const onRename = useCallback(async (row: Row) => {
    const finishLoading = parallelSaving.startLoading();
    try {
      const data = row.row.data!;
      const newName = await modals.showPrompt({
        defaultValue: data.value,
        title: i18n.t('data_blocks.types.rename_prompt.title'),
        text: i18n.t('data_blocks.types.rename_prompt.text'),
        fieldName: i18n.t('data_blocks.types.rename_prompt.field'),
        isMandatory: true,
      });
      if (newName && newName !== data.value) {
        await QueryFactory.DataBlockTypeClient.rename(data.id, {
          value: newName,
        });
        await refetchQuery();
      }
    } catch (error) {
      await modals.showError({ text: parseError(error) });
    } finally {
      finishLoading();
    }
  }, []);

  const onEditingStart = useCallback(
    (e: EditingStartEvent<DataBlockTypeListDto, number>) => {
      // we don't allow editing 'value' and 'variant' for existing data block types
      const row = e.data as DataBlockTypeListDto;
      if (Number.isInteger(row.id) && e.column?.dataField !== nameof('label')) {
        e.cancel = true;
      }
      if (
        row.variant !== DataBlockVariant.DeviceMeasurement &&
        e.column?.dataField === 'device'
      ) {
        e.cancel = true;
      }
    },
    [],
  );

  const onVariantChanged = useEventCallback(
    (data: DataBlockTypeListDto, variant: DataBlockVariant) => {
      data.variant = variant;
      if (variant !== DataBlockVariant.DeviceMeasurement) {
        data.device = null;
      }
    },
  );

  const onExportHandler = useDataGridExportHandler({
    fileName: 'DataBlockTypes',
  });

  const dataSource = useCachedDataSourceWhenEditing(
    dataGridRef,
    dataBlockTypesQuery.data,
  );
  return (
    <div className={styles.container} data-test-id="data-block-types-page">
      <input
        type="file"
        id="importExcelFile"
        ref={inputFileRef}
        className={'hidden'}
        onChange={onImportExcelFile}
      />
      <Loading
        title={
          isExcelLoading
            ? i18n.t('table.excel_loading')
            : i18n.t('data_blocks.loading')
        }
        loading={isExcelLoading || dataBlockTypesQuery.isLoading}
      >
        <AutoSizedTableWrapper>
          <DXG.DataGrid
            ref={dataGridRef}
            repaintChangesOnly={true}
            onRowInserting={onRowCreate}
            onRowRemoving={onRowDelete}
            onRowUpdating={onRowUpdate}
            dataSource={dataSource}
            keyExpr="id"
            export={{
              enabled: true,
            }}
            onExporting={onExportHandler}
            showBorders={true}
            onInitNewRow={onInitNewRow}
            onToolbarPreparing={onToolbarPrerendering}
            onOptionChanged={onOptionsChange}
            onEditorPreparing={onEditorPreparing}
            onEditingStart={onEditingStart}
            className="dxGridHighlightedOnHover"
            style={{ width: '100%' }}
          >
            <DXG.Paging enabled={false} />
            <DXG.Editing
              newRowPosition="pageTop"
              mode="batch"
              allowUpdating={true}
              allowAdding={claims.permissions.dataBlockTypeManagement}
              allowDeleting={claims.permissions.dataBlockTypeManagement}
              selectTextOnEditStart={true}
              startEditAction="click"
            />
            <DXG.SearchPanel
              visible={true}
              width={240}
              placeholder={i18n.t('table.search_placeholder')}
            />
            <DXG.Sorting mode="single" />
            <DXG.Column dataField="id" sortOrder="desc" visible={false} />
            <DXG.Column
              dataField="value"
              dataType="string"
              calculateDisplayValue={(type: DataBlockTypeListDto) => {
                if (type.variant === DataBlockVariant.SharedFields) {
                  return type.value;
                }
                const typeName = getDataBlockTypeNameForLocalization(type);
                return i18nDataBlockTypes.t(typeName as any, {
                  defaultValue: type.value,
                });
              }}
              caption={i18n.t('data_blocks.type')}
            >
              <DXG.RequiredRule />
            </DXG.Column>
            <DXG.Column
              dataField="label"
              dataType="string"
              caption={i18n.t('data_blocks.label')}
              allowEditing={claims.permissions.configureMeasurementUI}
            />
            <DXG.Column
              dataField="variant"
              dataType="number"
              caption={i18n.t('data_blocks.variant')}
              showEditorAlways={true}
              allowEditing={true}
              setCellValue={onVariantChanged}
            >
              <DXG.Lookup
                dataSource={dataBlockTypes}
                valueExpr="value"
                displayExpr="displayValue"
              />
              <DXG.RequiredRule />
            </DXG.Column>
            <DXG.Column
              dataField="device"
              dataType="number"
              caption={i18n.t('data_blocks.device_model')}
              allowEditing={true}
            >
              <DXG.Lookup
                dataSource={deviceModels}
                valueExpr="value"
                displayExpr="displayValue"
              />
              <DXG.CustomRule
                message={i18n.t('errors.required')}
                validationCallback={(item: any) => {
                  const data = item.data as DataBlockTypeListDto;
                  if (data.variant === DataBlockVariant.DeviceMeasurement) {
                    return !!data.device;
                  }
                  return true;
                }}
              />
            </DXG.Column>
            <DXG.Column type="buttons">
              {claims.permissions.dataBlockTypeManagement && (
                <DXG.Button name="delete" visible={isDeleteVisible as any} />
              )}
              {claims.permissions.deactivateDatablocks && (
                <DXG.Button
                  text={i18n.t('data_blocks.types.activate_button')}
                  visible={isActivateVisible as any}
                  onClick={onRowActivate as any}
                />
              )}
              {claims.permissions.deactivateDatablocks && (
                <DXG.Button
                  text={i18n.t('data_blocks.types.deactivate_button')}
                  visible={isDeactivateVisible as any}
                  onClick={onRowDeactivate as any}
                />
              )}
              {claims.permissions.dataBlockTypeManagement && (
                <DXG.Button
                  text={i18n.t('data_blocks.types.rename_button')}
                  visible={isRenameVisible as any}
                  onClick={onRename as any}
                />
              )}
            </DXG.Column>
          </DXG.DataGrid>
        </AutoSizedTableWrapper>
      </Loading>
    </div>
  );
});
